import { useCallback, useMemo } from "react";
import { notification } from "@excent-one/react/notification";
import type { IncidentRecordFileInfoDto } from "@phrmpc/infrastructure/phrmpc/models";
import type { FileUploaderPermissionMode as FileUploadPermissionMode } from "@excent-one/react/file-uploader";
import { useIncidentRecordFiles } from "../components/IncidentRecordFileContext";

/**
 * One case-document type's slot in the monitoring-phase TabStrip (Task 157786) - the unit a
 * single `TabStripTab` renders. `singleUploadOnly` slots (Case Summary) have no acknowledge/signed
 * sub-step at all; every other slot always has both.
 */
export interface CaseDocumentChainSlot {
  key: string;
  label: string;
  fileFieldTypeId: number;
  /** Distinguishes AWOL's NOH 1 (0) from NOH 2 (1), which share one fileFieldTypeId. */
  matchIndex: number;
  singleUploadOnly: boolean;
  /** Whether this tab is reachable yet, per the case type's chain sequencing. */
  tabEnabled: boolean;

  unsignedFile: IncidentRecordFileInfoDto | undefined;
  unsignedPermissionMode: FileUploadPermissionMode;
  isUnsignedConfirmed: boolean;
  uploadUnsigned: (
    file: File,
    onProgress?: (percent: number) => void,
  ) => Promise<{ name: string }>;
  deleteUnsigned: () => Promise<void>;
  confirmUnsigned: () => Promise<void>;

  isAcknowledged: boolean;
  /** Coordinator-only, per BA confirmation - LRD uploads/confirms the unsigned copy; the
   *  Coordinator records member acknowledgement and uploads the signed copy. */
  canEditAcknowledge: boolean;
  setAcknowledged: (value: boolean) => Promise<void>;

  signedFile: IncidentRecordFileInfoDto | undefined;
  signedPermissionMode: FileUploadPermissionMode;
  isSignedConfirmed: boolean;
  uploadSigned: (
    file: File,
    onProgress?: (percent: number) => void,
  ) => Promise<{ name: string }>;
  deleteSigned: () => Promise<void>;
  confirmSigned: () => Promise<void>;
}

interface SlotDefinition {
  key: string;
  label: string;
  fileFieldTypeId: number;
  matchIndex?: number;
  singleUploadOnly?: boolean;
  /** True once this slot's chain prerequisite is satisfied. */
  enabled: boolean;
  /** True once NOH1's unsigned step alone should stop counting toward NOH1's own remaining
   *  sub-steps - the AWOL NOH1/NOH2 supersession rule. */
  supersededByNextSlot?: boolean;
}

export interface UseCaseDocumentChainParams {
  incidentTypeName: string;
  fileFieldType: Record<string, number>;
  isUserLRD: boolean;
  isUserCoordinator: boolean;
  currentUserId: string | undefined;
  disciplinaryLocked: boolean;
  /** Bug 159910: TBWL's unsigned copy can also be uploaded by the Incident Report owner
   *  (the record's creator), not just LRD - every other AWOL document in this chain stays
   *  LRD-only for the unsigned step. Compared case-insensitively against currentUserId,
   *  matching MonitoringCaseSection.tsx's canEditResignationTermination. */
  recordCreatedBy: string | undefined;
}

/**
 * Derives the Kendo TabStrip's per-document-type slots for the Incident Management monitoring
 * phase (Minor/Major/AWOL case-document chains), replacing IncidentForm.tsx's inline `locked`
 * booleans (Task 157786 step 1). Each slot's `unsignedPermissionMode`/`signedPermissionMode` is
 * derived from: chain reachability, the acting role (LRD for unsigned; Coordinator for
 * acknowledge/signed, per BA confirmation), file ownership (`createdBy` vs `currentUserId` -
 * Task 157758), and confirmed state:
 * - Not yet reachable, or viewed by the non-acting party before confirmation: `restricted` (file
 *   name visible, content not previewable) if a file exists there, otherwise nothing to upload.
 * - The acting party, before confirming: `upload-delete`.
 * - Once confirmed (by anyone): `read-only` for everyone - the confirmed-lock is enforced
 *   server-side by `DeleteIncidentRecordFileValidator`, this just matches that in the UI.
 *
 * Sources file data and upload/delete/confirm/acknowledge operations from
 * IncidentRecordFileContext instead of building its own queries/mutations - must be called from
 * inside an IncidentRecordFileProvider.
 */
export const useCaseDocumentChain = ({
  incidentTypeName,
  fileFieldType,
  isUserLRD,
  isUserCoordinator,
  currentUserId,
  disciplinaryLocked,
  recordCreatedBy,
}: UseCaseDocumentChainParams) => {
  const { getFile, upload, remove, confirm, acknowledge } =
    useIncidentRecordFiles();

  const isMinorCase = incidentTypeName === "Minor Case";
  const isMajorCase = incidentTypeName === "Major Case";
  const isAwol = incidentTypeName === "AWOL";

  const isRecordOwner =
    Boolean(recordCreatedBy) &&
    recordCreatedBy?.toLowerCase() === currentUserId?.toLowerCase();

  // Bug 157826 fix: mirrors IncidentCaseChainRules.GetUploadPrerequisite (api repo) exactly -
  // that rule's HasSigned/HasAny checks are existence-only (a signed/any file is on record), not
  // isConfirmed. The previous hasSignedConfirmed required isConfirmed for every chain-progression
  // check (NOH/NODA/CaseSummary/TBWL's `enabled`), which is stricter than what the backend
  // actually enforces - it left the next step's tab looking locked in the UI even though the
  // backend would already accept an upload to it, until someone remembered to separately hit
  // "Submit" to confirm the prior step. 157826 was this exact symptom for AWOL's T/BWL tab; the
  // same mismatch existed identically in Minor/Major Case's NOH/NODA/Case Summary gating.
  const hasSignedFile = useCallback(
    (fileFieldTypeId: number, matchIndex = 0) =>
      Boolean(getFile(fileFieldTypeId, true, matchIndex)),
    [getFile],
  );

  const definitions = useMemo<SlotDefinition[]>(() => {
    if (isMinorCase) {
      const hasSignedNte = hasSignedFile(fileFieldType.NOTICE_OF_EXPLANATION);
      return [
        {
          key: "noticeOfExplanation",
          label: "Notice of Explanation",
          fileFieldTypeId: fileFieldType.NOTICE_OF_EXPLANATION,
          enabled: true,
        },
        {
          key: "noticeOfHearing",
          label: "Notice of Hearing",
          fileFieldTypeId: fileFieldType.NOTICE_OF_HEARING,
          enabled: hasSignedNte,
        },
        {
          key: "noticeOfDecisionAndAction",
          label: "Notice of Decision and Action",
          fileFieldTypeId: fileFieldType.NOTICE_OF_DECISION_AND_ACTION,
          enabled: hasSignedNte,
        },
      ];
    }

    if (isMajorCase) {
      const hasSignedNte = hasSignedFile(fileFieldType.NOTICE_OF_EXPLANATION);
      const hasSignedNoh = hasSignedFile(fileFieldType.NOTICE_OF_HEARING);
      const hasCaseSummary = Boolean(
        getFile(fileFieldType.CASE_SUMMARY, false, 0),
      );
      return [
        {
          key: "noticeOfExplanation",
          label: "Notice of Explanation",
          fileFieldTypeId: fileFieldType.NOTICE_OF_EXPLANATION,
          enabled: true,
        },
        {
          key: "noticeOfHearing",
          label: "Notice of Hearing",
          fileFieldTypeId: fileFieldType.NOTICE_OF_HEARING,
          enabled: hasSignedNte,
        },
        {
          key: "caseSummary",
          label: "Case Summary",
          fileFieldTypeId: fileFieldType.CASE_SUMMARY,
          singleUploadOnly: true,
          enabled: hasSignedNoh,
        },
        {
          key: "noticeOfDecisionAndAction",
          label: "Notice of Decision and Action",
          fileFieldTypeId: fileFieldType.NOTICE_OF_DECISION_AND_ACTION,
          // Bug 157743 fix: gated on Case Summary having *any* file (Case Summary is never
          // signed) - matches IncidentCaseChainRules.HasCaseSummary's HasAny check exactly.
          enabled: hasCaseSummary,
        },
      ];
    }

    if (isAwol) {
      const hasSignedAwolLetter = hasSignedFile(fileFieldType.AWOL_LETTER);
      const noh1UnsignedConfirmed = Boolean(
        getFile(fileFieldType.AWOL_NOTICE_OF_HEARING_1, false, 0)?.isConfirmed,
      );
      // Bug 157825 fix: this used to reuse noh1UnsignedConfirmed (NOH1's OWN confirmation) as
      // NOH1's supersededByNextSlot flag too - so the moment LRD confirmed NOH1's unsigned
      // upload (a completely normal step), NOH1 immediately marked itself superseded and locked
      // canEditAcknowledge, even though NOH2 had never been touched. The comment right below
      // always said "once NOH2's unsigned step is confirmed" - needs NOH2's own state, not NOH1's.
      const noh2UnsignedConfirmed = Boolean(
        getFile(fileFieldType.AWOL_NOTICE_OF_HEARING_1, false, 1)?.isConfirmed,
      );
      const tbwlEnabled =
        hasSignedFile(fileFieldType.AWOL_NOTICE_OF_HEARING_1, 0) ||
        hasSignedFile(fileFieldType.AWOL_NOTICE_OF_HEARING_1, 1);
      return [
        {
          key: "awolLetter",
          label: "AWOL Letter",
          fileFieldTypeId: fileFieldType.AWOL_LETTER,
          enabled: true,
        },
        {
          key: "awolNoticeOfHearing1",
          label: "Notice of Hearing 1",
          fileFieldTypeId: fileFieldType.AWOL_NOTICE_OF_HEARING_1,
          matchIndex: 0,
          enabled: hasSignedAwolLetter,
          // Once NOH2's unsigned step is confirmed, NOH1's ack/signed sub-steps stop accepting
          // further action - NOH2 starting supersedes it (per the BA-approved design). This one
          // stays isConfirmed-gated deliberately - it's a frontend-only workflow rule, not
          // mirroring any IncidentCaseChainRules prerequisite (NOH1/NOH2 aren't distinct types on
          // the backend at all).
          supersededByNextSlot: noh2UnsignedConfirmed,
        },
        {
          key: "awolNoticeOfHearing2",
          label: "Notice of Hearing 2",
          fileFieldTypeId: fileFieldType.AWOL_NOTICE_OF_HEARING_2,
          matchIndex: 1,
          // NOH2 unlocks once NOH1's unsigned step ALONE is confirmed - not full NOH1 completion.
          enabled: noh1UnsignedConfirmed,
        },
        {
          key: "transferBackToWorkLetter",
          label: "Transfer/Back to Work Letter",
          fileFieldTypeId: fileFieldType.TRANSFER_BACK_TO_WORK_LETTER,
          enabled: tbwlEnabled,
        },
      ];
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMinorCase, isMajorCase, isAwol, getFile, fileFieldType]);

  const slots = useMemo<CaseDocumentChainSlot[]>(
    () =>
      definitions.map((def) => {
        const matchIndex = def.matchIndex ?? 0;
        const unsignedFile = getFile(def.fileFieldTypeId, false, matchIndex);
        const signedFile = def.singleUploadOnly
          ? undefined
          : getFile(def.fileFieldTypeId, true, matchIndex);

        const isUnsignedConfirmed = Boolean(unsignedFile?.isConfirmed);
        const isSignedConfirmed = Boolean(signedFile?.isConfirmed);
        // Compared case-insensitively, matching DeleteIncidentRecordFileValidator.cs/
        // ConfirmIncidentRecordFileValidator.cs's own CreatedBy checks server-side (see
        // MonitoringCaseSection.tsx's canEditResignationTermination for the same fix).
        const isOwnUnsigned =
          Boolean(unsignedFile) &&
          unsignedFile!.createdBy?.toLowerCase() ===
            currentUserId?.toLowerCase();
        const isOwnSigned =
          Boolean(signedFile) &&
          signedFile!.createdBy?.toLowerCase() === currentUserId?.toLowerCase();

        const resolveMode = (
          reachable: boolean,
          isConfirmed: boolean,
          isActingParty: boolean,
          isOwn: boolean,
          hasFile: boolean,
        ): FileUploadPermissionMode => {
          // An early-exit path (Resignation, or Termination for AWOL) freezes every slot against
          // further upload/delete/confirm, but the chain's history (whatever was already
          // uploaded) stays visible/previewable rather than being hidden outright - the user
          // should still be able to see what happened before the early exit was taken.
          if (disciplinaryLocked) return hasFile ? "read-only" : "restricted";
          if (!reachable) return "restricted";
          if (isConfirmed) return "read-only";
          if (isActingParty && (isOwn || !hasFile)) return "upload-delete";
          return "restricted";
        };

        const unsignedReachable = def.enabled && !def.supersededByNextSlot;
        const signedReachable = unsignedReachable && isUnsignedConfirmed;

        const unsignedActingParty =
          def.key === "transferBackToWorkLetter"
            ? isUserLRD || isRecordOwner
            : isUserLRD;

        const unsignedPermissionMode = resolveMode(
          unsignedReachable,
          isUnsignedConfirmed,
          unsignedActingParty,
          isOwnUnsigned,
          Boolean(unsignedFile),
        );
        const signedPermissionMode = def.singleUploadOnly
          ? "restricted"
          : resolveMode(
              signedReachable,
              isSignedConfirmed,
              isUserCoordinator,
              isOwnSigned,
              Boolean(signedFile),
            );

        return {
          key: def.key,
          label: def.label,
          fileFieldTypeId: def.fileFieldTypeId,
          matchIndex,
          singleUploadOnly: Boolean(def.singleUploadOnly),
          tabEnabled: def.enabled,

          unsignedFile,
          unsignedPermissionMode,
          isUnsignedConfirmed,
          uploadUnsigned: (
            file: File,
            onProgress?: (percent: number) => void,
          ) => upload(file, def.fileFieldTypeId, false, onProgress),
          deleteUnsigned: () => remove(unsignedFile!.id!),
          confirmUnsigned: () => confirm(unsignedFile!.id!),

          isAcknowledged: Boolean(unsignedFile?.isAcknowledged),
          // Coordinator-only (per BA confirmation): LRD uploads/confirms the unsigned copy;
          // the Coordinator records member acknowledgement and uploads the signed copy - not
          // both roles, despite the acknowledgement step's earlier design allowing either.
          // Bug 157825 fix: editable once LRD has *uploaded* the unsigned copy, not once LRD has
          // additionally confirmed/submitted it - per INCIDENT_MANAGEMENT_SPECS.md's field spec
          // ("Member Acknowledge? (NOH) - Visible and Mandatory if NOH is uploaded"), the
          // Coordinator's acknowledge sub-step is gated on the unsigned file existing, not on
          // LRD's separate confirm/lock action. Still editable until the pair itself is
          // confirmed/locked, or the record has taken an early-exit path (Resignation/Termination).
          canEditAcknowledge:
            isUserCoordinator &&
            !disciplinaryLocked &&
            def.enabled &&
            !def.supersededByNextSlot &&
            Boolean(unsignedFile) &&
            !isSignedConfirmed,
          setAcknowledged: (value: boolean) =>
            acknowledge(unsignedFile!.id!, value),

          signedFile,
          signedPermissionMode,
          isSignedConfirmed,
          // Bug 159754: UploadIncidentRecordFileValidator.cs rejects a signed-copy upload until
          // the unsigned copy is acknowledged, but that 400 never carries a field-mappable shape
          // through ControlledFileUploader's onUpload - it just surfaces as the library's generic
          // "File(s) failed to upload." Check the same precondition client-side first so the user
          // sees what's actually wrong instead of a generic failure.
          uploadSigned: (
            file: File,
            onProgress?: (percent: number) => void,
          ) => {
            if (!unsignedFile?.isAcknowledged) {
              notification.error(
                "Please tick Member Acknowledge before uploading the signed copy.",
              );
              return Promise.reject(
                new Error(
                  "Member Acknowledge is required before uploading the signed copy.",
                ),
              );
            }
            return upload(
              file,
              def.fileFieldTypeId,
              true,
              onProgress,
              unsignedFile.id,
            );
          },
          deleteSigned: () => remove(signedFile!.id!),
          confirmSigned: () => confirm(signedFile!.id!),
        };
      }),
    [
      definitions,
      getFile,
      currentUserId,
      isUserLRD,
      isUserCoordinator,
      upload,
      remove,
      confirm,
      acknowledge,
      recordCreatedBy,
    ],
  );

  return { slots };
};
